import { httpV2 } from "../http";

const DEFAULT_WEBSOCKET_URL =
  "wss://c8cp9nllvl.execute-api.us-east-2.amazonaws.com/prod/";
const MESSAGE_ROUTE_PATTERN = /\/messages\/(?<thread_id>[0-9]{16}).*/;

export class IncomingEvent extends Event {
  constructor(message) {
    super("websocket-incoming", { bubbles: true, composed: true });
    this.message = message;
  }
}

export class IncomingStatus extends Event {
  constructor(status) {
    super("websocket-update", { bubbles: true, composed: true });
    this.status = status;
  }
}

export class OutgoingStatus extends Event {
  constructor(status) {
    super("websocket-status", { bubbles: true, composed: true });
    this.status = status;
  }
}

const personalInboxIdsFor = (user) => [
  ...new Set(
    (user?.user?.personal_inboxes || [])
      .map((personalInbox) => personalInbox?.id ?? personalInbox?.personal_inbox_id)
      .filter((id) => id !== null && id !== undefined)
      .map((id) => Number(id)),
  ),
];

export class ThreadStatus {
  constructor(
    user,
    {
      http = httpV2,
      WebSocketImpl = process.env.REACT_APP_ENV === "dev" && !process.env.REACT_APP_WEBSOCKET_URL
        ? null
        : window.WebSocket,
      windowObject = window,
      documentObject = document,
      websocketUrl =
        process.env.REACT_APP_WEBSOCKET_URL || DEFAULT_WEBSOCKET_URL,
      reconnectBaseMs = 1000,
      reconnectMaxMs = 30000,
    } = {},
  ) {
    if (!user) {
      console.warn("a user is required to initialize ThreadStatus");
      return;
    }

    this.user = user;
    this.http = http;
    this.WebSocketImpl = WebSocketImpl;
    this.window = windowObject;
    this.document = documentObject;
    this.websocketUrl = websocketUrl;
    this.reconnectBaseMs = reconnectBaseMs;
    this.reconnectMaxMs = reconnectMaxMs;
    this.personalInboxIds = personalInboxIdsFor(user);
    this.primaryPersonalInboxId = this.personalInboxIds[0] || null;
    this.status = {};
    this.conn = null;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.destroyed = false;

    this.handleStatusUpdate = (event) => {
      this.updateStatus(event.status);
      this.pushToRoom("update_status", event.status?.data);
    };
    this.handleNavigation = (event) => {
      this.publishLocationStatus(event?.destination?.url);
    };
    this.handleOnline = () => this.ensureConnected();
    this.handleVisibilityChange = () => {
      if (this.document.visibilityState === "visible") {
        this.ensureConnected(true);
      }
    };

    this.window.addEventListener("websocket-update", this.handleStatusUpdate);
    this.window.addEventListener("online", this.handleOnline);
    this.document.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.window.navigation?.addEventListener(
      "navigate",
      this.handleNavigation,
    );

    if (!this.primaryPersonalInboxId || !this.WebSocketImpl) {
      return;
    }

    this.updateStatus(this.statusForLocation());
    this.connect();
  }

  statusForLocation(url) {
    const locationUrl = url ? new URL(url, this.window.location.href) : null;
    const pathname = locationUrl?.pathname || this.window.location.pathname;
    const matches = pathname.match(MESSAGE_ROUTE_PATTERN);
    const data = {
      personal_inbox_id: this.primaryPersonalInboxId,
      status: "browsing",
    };

    if (matches?.groups?.thread_id) {
      data.thread_id = Number(matches.groups.thread_id);
      data.status = "viewing";
    }

    return { action: "update_status", data };
  }

  publishLocationStatus(url) {
    if (!this.primaryPersonalInboxId) return;
    const message = this.statusForLocation(url);
    this.updateStatus(message);
    this.pushToRoom("update_status", message.data);
  }

  connect() {
    if (this.destroyed || !this.WebSocketImpl || !this.primaryPersonalInboxId) {
      return;
    }

    const existingConnection = this.conn;
    if (
      existingConnection &&
      [this.WebSocketImpl.OPEN, this.WebSocketImpl.CONNECTING].includes(
        existingConnection.readyState,
      )
    ) {
      this.conn = null;
      existingConnection.close();
    }

    const connection = new this.WebSocketImpl(this.websocketUrl);
    this.conn = connection;

    connection.addEventListener("open", () => {
      if (this.destroyed || this.conn !== connection) return;
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
      this.joinPersonalInboxes(connection);
      this.window.dispatchEvent(new Event("websocket-connect"));
      this.pushToRoom("echo_status", this.currentStatusPayload());
    });

    connection.addEventListener("message", (event) => {
      if (this.destroyed || this.conn !== connection || event.data === "") {
        return;
      }

      try {
        const message = JSON.parse(event.data);
        if (["update_status", "echo_status"].includes(message.action)) {
          this.updateStatus(message);
        } else {
          this.window.dispatchEvent(new IncomingEvent(message));
        }
      } catch (error) {
        console.warn("Ignoring malformed WebSocket message", error);
      }
    });

    connection.addEventListener("close", () => {
      if (this.conn !== connection) return;
      this.conn = null;
      this.scheduleReconnect();
    });

    connection.addEventListener("error", () => {
      if (this.conn !== connection) return;
      this.scheduleReconnect();
      try {
        connection.close();
      } catch (error) {
        console.warn("Unable to close failed WebSocket", error);
      }
    });
  }

  joinPersonalInboxes(connection = this.conn) {
    if (!connection || connection.readyState !== this.WebSocketImpl.OPEN) {
      return;
    }

    this.personalInboxIds.forEach((personalInboxId) => {
      connection.send(
        JSON.stringify({
          service: "notifications",
          action: "join",
          data: { room: personalInboxId, token: this.document.cookie?.split('; ').find(value => value.startsWith('api_token='))?.slice('api_token='.length) },
        }),
      );
    });
  }

  ensureConnected(resynchronize = false) {
    if (this.destroyed || !this.WebSocketImpl || !this.primaryPersonalInboxId) return;

    if (this.conn?.readyState === this.WebSocketImpl.OPEN) {
      if (resynchronize) {
        this.joinPersonalInboxes();
        this.window.dispatchEvent(new Event("websocket-connect"));
        this.pushToRoom("echo_status", this.currentStatusPayload());
      }
      return;
    }

    if (this.conn?.readyState === this.WebSocketImpl.CONNECTING) return;

    this.clearReconnectTimer();
    this.connect();
  }

  scheduleReconnect() {
    if (this.destroyed || this.reconnectTimer) return;

    const delay = Math.min(
      this.reconnectMaxMs,
      this.reconnectBaseMs * 2 ** this.reconnectAttempt,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = this.window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  clearReconnectTimer() {
    if (!this.reconnectTimer) return;
    this.window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  updateStatus(message) {
    const { action, data } = message || {};
    if (!data?.personal_inbox_id || !data.status) return;

    const personalInboxId = Number(data.personal_inbox_id);
    const status = this.status[personalInboxId] || {
      viewing: null,
      typing: null,
      read: [],
    };

    if (data.status === "viewing") {
      status.viewing = Number(data.thread_id);
      status.typing = null;
      if (!status.read.includes(Number(data.thread_id))) {
        status.read.push(Number(data.thread_id));
      }
    } else if (data.status === "browsing") {
      status.viewing = null;
      status.typing = null;
    } else if (data.status === "typing") {
      status.viewing = Number(data.thread_id);
      status.typing = Number(data.thread_id);
    }

    this.status[personalInboxId] = status;

    if (action === "echo_status") {
      this.pushToRoom("update_status", this.currentStatusPayload());
    }

    this.window.dispatchEvent(new OutgoingStatus({ ...this.status }));
  }

  currentStatusPayload() {
    const current = this.status[this.primaryPersonalInboxId] || {};
    const payload = {
      thread_id: null,
      personal_inbox_id: this.primaryPersonalInboxId,
      status: "browsing",
    };

    if (current.viewing && !current.typing) {
      payload.status = "viewing";
      payload.thread_id = current.viewing;
    } else if (current.typing) {
      payload.status = "typing";
      payload.thread_id = current.typing;
    }

    return payload;
  }

  pushToRoom(action, data) {
    if (!this.WebSocketImpl || !data?.personal_inbox_id) return Promise.resolve();

    return this.http
      .post("utils/push_to_room", { action, data })
      .catch((error) => {
        console.warn("Unable to publish WebSocket status", error);
      });
  }

  destroy() {
    this.destroyed = true;
    this.clearReconnectTimer();
    this.window.removeEventListener(
      "websocket-update",
      this.handleStatusUpdate,
    );
    this.window.removeEventListener("online", this.handleOnline);
    this.document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.window.navigation?.removeEventListener(
      "navigate",
      this.handleNavigation,
    );

    const connection = this.conn;
    this.conn = null;
    if (
      connection &&
      connection.readyState !== this.WebSocketImpl.CLOSED &&
      connection.readyState !== this.WebSocketImpl.CLOSING
    ) {
      connection.close(1000, "ThreadStatus destroyed");
    }
  }
}
